import React from 'react'
import {
  Box,
  Typography,
  Button,
  Container,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import { useHandleChangeRoute } from 'data/hooks'
import BackgroundVideo from 'static/videos/mystethi-video-overlay1.mp4'
import BackgroundVideoMobile from 'static/videos/mystethi-video-overlay_mobile.mp4'

const NewHeroSection = () => {
  const theme = useTheme()
  const handleChangeRoute = useHandleChangeRoute()
  const belowSm = useMediaQuery(theme.breakpoints.down('sm'))
  const video = belowSm ? BackgroundVideoMobile : BackgroundVideo

  return (
    <Box
      sx={{
        backgroundColor: theme.palette.background.dark,
        color: theme.palette.common.white,
        py: { xs: 12, md: 18 },
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <Box
        component="video"
        src={video}
        autoPlay
        muted
        loop
        playsInline
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          zIndex: 0,
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundColor: 'rgba(44, 62, 80, 0.55)',
          zIndex: 1,
        }}
      />
      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }}>
        <Box
          sx={{
            maxWidth: '720px',
            mx: 'auto',
            textAlign: 'center',
          }}
        >
          <Typography
            variant="overline"
            sx={{
              color: theme.palette.primary.light,
              fontWeight: 600,
              letterSpacing: '0.15em',
              fontSize: '0.85rem',
              display: 'block',
              mb: 2,
            }}
          >
            Built by Clinicians, for Clinicians
          </Typography>

          <Typography
            variant="h1"
            sx={{
              fontSize: { xs: '2.75rem', md: '3.75rem', lg: '4.5rem' },
              fontWeight: 700,
              color: theme.palette.common.white,
              lineHeight: 1.15,
              mb: 3,
              textShadow: '0 2px 12px rgba(0,0,0,0.3)',
            }}
          >
            Find the job that fits{' '}
            <Box
              component="span"
              sx={{ color: theme.palette.primary.light }}
            >
              your whole life.
            </Box>
          </Typography>

          <Typography
            variant="h2"
            sx={{
              color: 'rgba(255,255,255,0.55)',
              mb: 5,
              fontSize: { xs: '1.1rem', md: '1.25rem' },
              fontWeight: 400,
              lineHeight: 1.7,
            }}
          >
            Search across the role, the schedule, the city, the schools. Then talk to the hospital, directly.
          </Typography>

          <Box
            sx={{
              display: 'flex',
              gap: 2,
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Button
              variant="contained"
              size="large"
              onClick={() => handleChangeRoute('/register/physicians')()}
              sx={{
                borderRadius: '8px',
                px: { xs: 4, sm: 5 },
                py: { xs: 1.5, sm: 2 },
                fontSize: { xs: '1rem', sm: '1.1rem' },
                fontWeight: 600,
                backgroundColor: theme.palette.primary.main,
                color: theme.palette.common.white,
                boxShadow: theme.palette.shadows.colored.primary,
                textTransform: 'none',
                minWidth: { xs: '250px', sm: '200px' },
                width: { xs: '100%', sm: 'auto' },
                maxWidth: { xs: '300px', sm: 'none' },
                '&:hover': {
                  backgroundColor: theme.palette.primary.dark,
                  boxShadow: theme.palette.shadows.colored.primaryHover,
                  transform: 'translateY(-2px)',
                }
              }}
            >
              Find Your Next Role
            </Button>
            <Button
              variant="outlined"
              size="large"
              onClick={() => handleChangeRoute('/jobs')()}
              sx={{
                borderRadius: '8px',
                px: { xs: 4, sm: 5 },
                py: { xs: 1.5, sm: 2 },
                fontSize: { xs: '1rem', sm: '1.1rem' },
                fontWeight: 600,
                color: theme.palette.common.white,
                borderColor: 'rgba(255,255,255,0.3)',
                textTransform: 'none',
                minWidth: { xs: '250px', sm: '200px' },
                width: { xs: '100%', sm: 'auto' },
                maxWidth: { xs: '300px', sm: 'none' },
                '&:hover': {
                  borderColor: theme.palette.common.white,
                  backgroundColor: 'rgba(255,255,255,0.08)',
                }
              }}
            >
              Browse Jobs
            </Button>
          </Box>

          <Box sx={{ mt: 3 }}>
            <Typography
              component="span"
              onClick={() => handleChangeRoute('/recruiters')()}
              sx={{
                color: 'rgba(255,255,255,0.55)',
                fontSize: '0.9rem',
                cursor: 'pointer',
                textDecoration: 'underline',
                textUnderlineOffset: '3px',
                '&:hover': {
                  color: 'rgba(255,255,255,0.85)',
                }
              }}
            >
              Are you a health system?
            </Typography>
          </Box>
        </Box>
      </Container>
    </Box>
  )
}

export default NewHeroSection
